<template>
  <v-text-field
    v-model="value"
    autocomplete="off"
    base-color="var(--color-gray-900)"
    :color="props.color"
    :error="props.error"
    :error-messages="props.errorMessages"
    hide-details="auto"
    :label="props.label"
    persistent-placeholder
    :placeholder="props.placeholder || props.label"
    :prefix="props.prefix"
    variant="outlined"
    @update:model-value="onChange"
  />
</template>

<script lang="ts" setup>
  import { ref, computed, watch, defineOptions, onMounted, nextTick } from 'vue';
  import { VTextField } from 'vuetify/components';

  type Value = any;

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(
    defineProps<{
      modelValue: Value
      size?: 'sm' | 'md' | 'lg' | 'xl'
      label?: string
      placeholder?: string
      prefix?: string
      color?: string
      error?: boolean
      errorMessages?: string | string[]
    }>(),
    {
      size: 'lg',
      label: undefined,
      placeholder: undefined,
      prefix: undefined,
      color: 'var(--color-blue-500)',
      error: false,
      errorMessages: [],
    },
  );

  const emit = defineEmits<{
    (emit: 'on-change', value: Value): void
  }>();

  const value = ref<Value>(props.modelValue);

  const height = computed((): string => {
    switch (props.size) {
      case 'sm':
        return '1.75rem';
      case 'md':
        return '1.5rem';
      case 'xl':
        return '4rem';
      case 'lg':
      default:
        return '3rem';
    }
  });

  watch(
    () => props.modelValue,
    (newValue: Value) => {
      value.value = newValue;
    },
  );

  const onChange = (newValue: Value): void => {
    value.value = newValue;
    emit('on-change', newValue);
  };

  onMounted(() => {
    nextTick(() => {
      value.value = props.modelValue;
    });
  });
</script>

<style lang="scss" scoped>
:deep(.v-field) {
  --color-gray-900: oklch(21% 0.034 264.665);
  --color-blue-500: var(--color-base-500);
  height: v-bind(height);

  .v-text-field__prefix,
  .v-field__input {
    padding-block: 0;
    min-height: auto;
  }
  .v-field__field,
  .v-field__field > * {
    height: v-bind(height);
  }

  .v-field-label--floating {
    --v-field-label-scale: 0.75em;
  }
}
</style>
